// Mi Perfil — el cliente edita tres de los cuatro campos del Perfil Aduanero.
//
// La escritura NO va por PostgREST directo: en desarrollos_perfil_aduanero la
// única política que alcanza al rol anon es `desarrollos_expo_anon_perfil_read`
// (SELECT). No hay política de UPDATE para anon, así que un update directo
// fallaría contra RLS en silencio. El guardado pasa por el RPC
// `desarrollos_expo_update_perfil_client` (SECURITY DEFINER, EXECUTE a anon),
// que acota el UPDATE al tenant de Desarrollos y a los tres campos del cliente.
//
// no_registro queda fuera del RPC a propósito — lo asigna el operador. Por eso
// acá se muestra como texto, nunca como <input>: un campo editable que no
// guarda es peor que un campo que no se puede tocar.
//
// El guardado es explícito (botón), no autosave por blur: el cliente tiene que
// ver el momento del commit.
//
// financial_config y estado no se consultan ni se muestran nunca (D12).

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { DESARROLLOS_TENANT_ID } from '../tenant';
import SivarSendWordmark from '../../components/brand/SivarSendWordmark';
import { useCliente } from './ClienteContext';
import ClienteActions from './ClienteActions';

// Los tres que el RPC acepta. no_registro se renderiza aparte, de solo lectura.
const EDITABLES = [
  { key: 'codigo_gestor', label: 'Código de Gestor', placeholder: 'GEST-0000' },
  { key: 'codigo_aduana', label: 'Código de Aduana', placeholder: '00000' },
  { key: 'nombre_gestor', label: 'Nombre del Gestor', placeholder: 'Nombre y apellido' },
];

const VACIO = { codigo_gestor: '', codigo_aduana: '', nombre_gestor: '' };

const aFormulario = (row) => ({
  codigo_gestor: row?.codigo_gestor || '',
  codigo_aduana: row?.codigo_aduana || '',
  nombre_gestor: row?.nombre_gestor || '',
});

const MiPerfil = ({ onNavigate }) => {
  const { cliente } = useCliente();
  const [perfil, setPerfil] = useState(null);
  const [estado, setEstado] = useState('cargando'); // cargando | listo | vacio | error

  // `cargado` es la referencia contra la que se compara para habilitar Guardar.
  // Se reemplaza después de cada guardado exitoso para que el botón se vuelva
  // a deshabilitar sin necesidad de recargar.
  const [cargado, setCargado] = useState(VACIO);
  const [form, setForm] = useState(VACIO);

  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState('');
  const [okMontado, setOkMontado] = useState(false);
  const [okDesvanecido, setOkDesvanecido] = useState(false);
  const temporizadores = useRef([]);

  const limpiarTemporizadores = () => {
    temporizadores.current.forEach(clearTimeout);
    temporizadores.current = [];
  };

  useEffect(() => () => limpiarTemporizadores(), []);

  useEffect(() => {
    if (!cliente) {
      onNavigate('/desarrollos');
      return undefined;
    }
    let cancelado = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('desarrollos_perfil_aduanero')
          .select('codigo_gestor, codigo_aduana, nombre_gestor, no_registro')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .eq('client_id', cliente.id)
          .maybeSingle();
        if (error) throw error;
        if (cancelado) return;
        setPerfil(data || null);
        setCargado(aFormulario(data));
        setForm(aFormulario(data));
        setEstado(data ? 'listo' : 'vacio');
      } catch (err) {
        console.error('[Desarrollos/mi-perfil] error cargando el perfil', err);
        if (!cancelado) setEstado('error');
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [cliente, onNavigate]);

  const sucio = useMemo(
    () => EDITABLES.some((c) => form[c.key].trim() !== cargado[c.key]),
    [form, cargado]
  );

  const cambiar = (key) => (e) => {
    setErrorGuardar('');
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const guardar = async () => {
    if (!sucio || guardando) return;
    limpiarTemporizadores();
    setOkMontado(false);
    setErrorGuardar('');
    setGuardando(true);

    const valores = {
      codigo_gestor: form.codigo_gestor.trim(),
      codigo_aduana: form.codigo_aduana.trim(),
      nombre_gestor: form.nombre_gestor.trim(),
    };

    try {
      const { error } = await supabase.rpc('desarrollos_expo_update_perfil_client', {
        p_client_id: cliente.id,
        p_codigo_gestor: valores.codigo_gestor,
        p_codigo_aduana: valores.codigo_aduana,
        p_nombre_gestor: valores.nombre_gestor,
      });
      if (error) throw error;

      // La referencia se mueve al valor recién guardado: el botón se apaga solo.
      setForm(valores);
      setCargado(valores);
      setPerfil((prev) => ({ ...(prev || {}), ...valores }));

      setOkDesvanecido(false);
      setOkMontado(true);
      temporizadores.current.push(setTimeout(() => setOkDesvanecido(true), 2000));
      temporizadores.current.push(setTimeout(() => setOkMontado(false), 2400));
    } catch (err) {
      console.error('[Desarrollos/mi-perfil] error guardando el perfil', err);
      setErrorGuardar('No se pudo guardar. Intentá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  if (!cliente) return null;

  return (
    <div className="min-h-screen bg-[#f8fafc] p-5">
      <div className="mx-auto max-w-2xl pt-6">
        <div className="flex items-start justify-between gap-4">
          <SivarSendWordmark size="md" />
          <ClienteActions onNavigate={onNavigate} />
        </div>

        <button
          type="button"
          onClick={() => onNavigate('/desarrollos/cliente')}
          className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 text-xs font-bold text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft size={14} /> Volver
        </button>

        <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
          <h1 className="text-base font-bold text-slate-900">Tu Perfil Aduanal</h1>
          <p className="mt-2 text-xs leading-relaxed text-slate-500">
            Editá tu información aduanal. Guardá cuando termines.
          </p>

          {estado === 'cargando' && (
            <div className="mt-5 space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
              ))}
            </div>
          )}

          {estado === 'error' && (
            <p className="mt-5 text-xs text-red-600">
              No pudimos cargar tu perfil. Revisá tu señal y volvé a intentar.
            </p>
          )}

          {estado === 'vacio' && (
            <p className="mt-5 text-xs text-slate-400">
              Todavía no tenés un perfil registrado. Tu operador lo va a completar con vos.
            </p>
          )}

          {estado === 'listo' && (
            <>
              <div className="mt-5 space-y-4">
                {EDITABLES.map((c) => (
                  <div key={c.key} className="flex flex-col gap-1">
                    <label
                      htmlFor={`perfil-${c.key}`}
                      className="text-[10px] font-semibold uppercase tracking-wide text-slate-400"
                    >
                      {c.label}
                    </label>
                    <input
                      id={`perfil-${c.key}`}
                      type="text"
                      autoComplete="off"
                      value={form[c.key]}
                      onChange={cambiar(c.key)}
                      placeholder={c.placeholder}
                      className="h-11 w-full rounded-lg border border-slate-200 bg-[#f8fafc] px-3 text-sm text-slate-900 outline-none transition-colors focus:border-[var(--brand-teal)]"
                    />
                  </div>
                ))}

                {/* no_registro: lo asigna el operador, el RPC no lo acepta. */}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    No. de Registro
                  </p>
                  <p className="mt-1 text-sm text-slate-800">{perfil?.no_registro || '—'}</p>
                  <p className="mt-1 text-xs text-slate-400">
                    Contactá a tu operador para actualizar este campo.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                {okMontado && (
                  <span
                    className={`text-xs font-semibold text-emerald-600 transition-opacity duration-300 ${
                      okDesvanecido ? 'opacity-0' : 'opacity-100'
                    }`}
                  >
                    Guardado
                  </span>
                )}
                {errorGuardar && (
                  <span className="text-xs font-semibold text-red-600">{errorGuardar}</span>
                )}
                <button
                  type="button"
                  onClick={guardar}
                  disabled={!sucio || guardando}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-[var(--brand-teal)] px-5 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-teal-hover)] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                >
                  {guardando && <Loader2 size={14} className="animate-spin" />}
                  Guardar cambios
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default MiPerfil;
